import { useCallback, useEffect, useState } from 'react';
import { Card, StatusBadge, Tabs } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { superApi } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

/* לוגים מערכתיים: כל שימוש ב-API הציבורי + כל משלוחי ה-Webhooks.
   מוצג כלשונית במסך לוג האירועים (/super/audit). */

interface TenantOpt { id: number; name: string }

interface ApiLogRow {
  id: number; tenant_id: number | null; tenant_name: string | null; key_name: string | null;
  method: string; path: string; status: number; ip: string | null;
  duration_ms: number | null; error: string | null; created_at: string;
}
interface HookLogRow {
  id: number; tenant_id: number | null; tenant_name: string | null; webhook_url: string | null;
  event: string; status: string; attempts: number; http_status: number | null;
  latency_ms: number | null; response_snippet: string | null; created_at: string;
}

const HOOK_STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  success: 'ok', pending: 'neutral', failed: 'warn', dead: 'danger',
};
const HOOK_STATUS_HE: Record<string, string> = {
  success: 'הצליח', pending: 'ממתין', failed: 'נכשל', dead: 'מת',
};

const fmtTime = (s: string) => new Date(s).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });

export function ApiUsageLogs() {
  const [tab, setTab] = useState<'api' | 'webhooks'>('api');
  const [tenantId, setTenantId] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<unknown[]>([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [logsLoading, setLogsLoading] = useState(false);
  const [tenants, setTenants] = useState<TenantOpt[]>([]);

  useEffect(() => {
    void superApi.tenants({ per_page: 500 }).then((r) => setTenants((r.data as TenantOpt[]) ?? [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params: Record<string, unknown> = { page };
      if (tenantId) params.tenant_id = tenantId;
      if (status) params.status = status;
      const res = tab === 'api' ? await superApi.apiRequestLogs(params) : await superApi.apiWebhookLogs(params);
      setRows(res.data);
      setMeta(res.meta);
    } catch {
      toast('שגיאה בטעינת הלוגים', 'danger');
    } finally {
      setLogsLoading(false);
    }
  }, [tab, tenantId, status, page]);

  useEffect(() => { void load(); }, [load]);
  // רענון עדין כל 15 שניות — רק כשהטאב גלוי.
  useEffect(() => {
    const t = window.setInterval(() => { if (!document.hidden) void load(); }, 15000);
    return () => window.clearInterval(t);
  }, [load]);

  function switchTab(id: string) {
    setTab(id as 'api' | 'webhooks');
    setStatus('');
    setPage(1);
  }

  const statusOptions = tab === 'api'
    ? [['', 'הכל'], ['ok', 'תקין'], ['error', 'שגיאות']]
    : [['', 'הכל'], ['pending', 'ממתין'], ['success', 'הצליח'], ['failed', 'נכשל'], ['dead', 'מת']];

  return (
    <Card title={`שימוש ב-API ו-Webhooks (${meta.total})`}>
      <div className="stack">
        <Tabs
          size="sm"
          value={tab}
          onChange={switchTab}
          items={[
            { id: 'api', label: 'קריאות API' },
            { id: 'webhooks', label: 'משלוחי Webhooks' },
          ]}
        />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="select" value={tenantId} onChange={(e) => { setTenantId(e.target.value); setPage(1); }} style={{ maxWidth: 220 }}>
            <option value="">כל הלקוחות</option>
            {tenants.map((t) => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
          </select>
          <select className="select" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ maxWidth: 160 }}>
            {statusOptions.map(([v, he]) => <option key={v} value={v}>{he}</option>)}
          </select>
          <button type="button" className="btn" onClick={() => void load()} disabled={logsLoading}>
            <Icon name="history" size={13} /> רענן
          </button>
        </div>

        {rows.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)', padding: '8px 0' }}>
            {logsLoading ? 'טוען…' : tab === 'api' ? 'אין עדיין קריאות API מתועדות.' : 'אין עדיין משלוחי webhooks.'}
          </div>
        ) : tab === 'api' ? (
          <LogGrid
            head={['זמן', 'לקוח', 'מפתח', 'בקשה', 'סטטוס', 'משך', 'IP', 'שגיאה']}
            rows={(rows as ApiLogRow[]).map((r) => [
              <span key="t" dir="ltr" style={{ fontSize: 'var(--t-xs)', whiteSpace: 'nowrap' }}>{fmtTime(r.created_at)}</span>,
              r.tenant_name ?? '—',
              r.key_name ?? '—',
              <code key="p" dir="ltr" style={{ fontSize: 'var(--t-xs)' }}>{r.method} {r.path}</code>,
              <StatusBadge key="s" tone={r.status < 400 ? 'ok' : 'danger'} size="sm">{r.status}</StatusBadge>,
              r.duration_ms !== null ? <span key="d" dir="ltr">{r.duration_ms}ms</span> : '—',
              <span key="i" dir="ltr" style={{ fontSize: 'var(--t-xs)' }}>{r.ip ?? '—'}</span>,
              r.error ? <code key="e" dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--danger)' }}>{r.error}</code> : '—',
            ])}
          />
        ) : (
          <LogGrid
            head={['זמן', 'לקוח', 'אירוע', 'יעד', 'סטטוס', 'ניסיונות', 'HTTP', 'זמן תגובה']}
            rows={(rows as HookLogRow[]).map((r) => [
              <span key="t" dir="ltr" style={{ fontSize: 'var(--t-xs)', whiteSpace: 'nowrap' }}>{fmtTime(r.created_at)}</span>,
              r.tenant_name ?? '—',
              <code key="e" dir="ltr" style={{ fontSize: 'var(--t-xs)' }}>{r.event}</code>,
              <span key="u" dir="ltr" style={{ fontSize: 'var(--t-xs)', maxWidth: 220, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.webhook_url ?? ''}>{r.webhook_url ?? '—'}</span>,
              <StatusBadge key="s" tone={HOOK_STATUS_TONE[r.status] ?? 'neutral'} size="sm">{HOOK_STATUS_HE[r.status] ?? r.status}</StatusBadge>,
              r.attempts,
              r.http_status ?? '—',
              r.latency_ms !== null ? <span key="l" dir="ltr">{r.latency_ms}ms</span> : '—',
            ])}
          />
        )}

        {meta.last_page > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
            <button type="button" className="btn" disabled={page <= 1 || logsLoading} onClick={() => setPage((p) => p - 1)}>הקודם</button>
            <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>עמוד {meta.current_page} מתוך {meta.last_page}</span>
            <button type="button" className="btn" disabled={page >= meta.last_page || logsLoading} onClick={() => setPage((p) => p + 1)}>הבא</button>
          </div>
        )}
      </div>
    </Card>
  );
}

function LogGrid({ head, rows }: { head: React.ReactNode[]; rows: React.ReactNode[][] }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)' }}>
            {head.map((h, i) => (
              <th key={i} style={{ padding: '8px 12px', textAlign: 'start', fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 600 }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: '1px solid var(--line)' }}>
              {r.map((c, j) => (
                <td key={j} style={{ padding: '8px 12px', fontSize: 'var(--t-sm)', verticalAlign: 'middle' }}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
